import api from './api';
import type {
  PriceList,
  CreatePriceListData,
  UpdatePriceListData,
  PriceListFilters,
  PriceListResponse,
  PriceListSummary,
  ProductPriceDetails,
  ProductPriceRequest,
  ProductPriceResponse,
} from '../types/priceList';

const BASE_URL = '/price-lists';

export const priceListService = {
  async getAllPriceLists(filters: PriceListFilters = {}): Promise<PriceListResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`${BASE_URL}?${params.toString()}`);
    return response.data;
  },

  async getSummary(): Promise<PriceListSummary> {
    const response = await api.get<PriceListSummary>(`${BASE_URL}/summary`);
    return response.data;
  },

  // The price a customer gets for a product, or null when no active list covers it
  async getProductPriceDetails(request: ProductPriceRequest): Promise<ProductPriceDetails | null> {
    const params = {
      ...(request.customerId ? { customerId: request.customerId } : {}),
      ...(request.quantity ? { quantity: request.quantity } : {}),
    };
    const response = await api.get<ProductPriceDetails | ''>(
      `${BASE_URL}/product/${request.productId}/price-details`,
      { params },
    );
    return response.data || null;
  },

  async getActivePriceLists(): Promise<PriceList[]> {
    const response = await api.get(`${BASE_URL}/active`);
    return response.data;
  },

  async getPriceListById(id: string): Promise<PriceList> {
    const response = await api.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  async createPriceList(data: CreatePriceListData): Promise<PriceList> {
    const response = await api.post(BASE_URL, data);
    return response.data;
  },

  async updatePriceList(id: string, data: UpdatePriceListData): Promise<PriceList> {
    const response = await api.patch(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  async deletePriceList(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  async activatePriceList(id: string): Promise<PriceList> {
    const response = await api.patch(`${BASE_URL}/${id}/activate`);
    return response.data;
  },

  async deactivatePriceList(id: string): Promise<PriceList> {
    const response = await api.patch(`${BASE_URL}/${id}/deactivate`);
    return response.data;
  },

  async getProductPrice(request: ProductPriceRequest): Promise<ProductPriceResponse> {
    const params = new URLSearchParams();
    if (request.customerId) params.append('customerId', request.customerId);
    if (request.quantity) params.append('quantity', request.quantity.toString());

    const response = await api.get(`${BASE_URL}/product/${request.productId}/price?${params.toString()}`);
    return response.data;
  },
};

export default priceListService;